import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaCookie, 
  FaShieldAlt, 
  FaUserLock, 
  FaDatabase, 
  FaChartBar,
  FaCog,
  FaCheck,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaExternalLinkAlt
} from 'react-icons/fa';
import './CookiePolicy.css';

const CookiePolicy = () => {
  const [activeSection, setActiveSection] = useState('essential');
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  // Load saved preferences from localStorage
  useEffect(() => {
    const savedPreferences = localStorage.getItem('hrc-cookie-preferences');
    if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = (prefs) => {
    localStorage.setItem('hrc-cookie-preferences', JSON.stringify(prefs));
    setCookiePreferences(prefs);
  };

  const handleCookieToggle = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    
    const newPreferences = {
      ...cookiePreferences,
      [type]: !cookiePreferences[type]
    };
    savePreferences(newPreferences);
  };

  const acceptAllCookies = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    savePreferences(allAccepted);
  };

  const rejectAllCookies = () => {
    const allRejected = {
      essential: true, // Essential cookies are always enabled
      analytics: false,
      marketing: false,
      preferences: false
    };
    savePreferences(allRejected);
  };

  const saveCustomPreferences = () => {
    savePreferences(cookiePreferences);
    alert('Your cookie preferences have been saved successfully!');
  };

  const cookieTypes = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'Required for the basic functioning of the website. These cannot be disabled as they are necessary for security, authentication, and core features.',
      alwaysActive: true,
      examples: ['Session management', 'Security tokens', 'Load balancing'],
      icon: <FaShieldAlt />
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website by collecting anonymous information. This data helps us improve our services and user experience.',
      examples: ['Page visits', 'User behavior', 'Feature usage', 'Performance metrics'],
      icon: <FaChartBar />
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.',
      examples: ['Ad targeting', 'Conversion tracking', 'Remarketing', 'Campaign analysis'],
      icon: <FaUserLock />
    },
    {
      id: 'preferences',
      name: 'Preference Cookies',
      description: 'Allow the website to remember choices you make and provide enhanced, more personal features.',
      examples: ['Language settings', 'Theme preferences', 'Regional settings', 'Custom layouts'],
      icon: <FaCog />
    }
  ];

  const cookieDetails = [
    {
      name: '_hrc_session',
      purpose: 'Maintains user session state across page requests',
      duration: 'Session',
      type: 'Essential'
    },
    {
      name: '_hrc_csrf',
      purpose: 'Protects against Cross-Site Request Forgery attacks',
      duration: 'Session',
      type: 'Essential'
    },
    {
      name: '_ga',
      purpose: 'Google Analytics - Distinguishes unique users',
      duration: '2 years',
      type: 'Analytics'
    },
    {
      name: '_gid',
      purpose: 'Google Analytics - Distinguishes unique users',
      duration: '24 hours',
      type: 'Analytics'
    },
    {
      name: '_gat',
      purpose: 'Google Analytics - Throttles request rate',
      duration: '1 minute',
      type: 'Analytics'
    },
    {
      name: 'hrc_preferences',
      purpose: 'Stores user interface preferences',
      duration: '1 year',
      type: 'Preferences'
    }
  ];

  return (
    <div className="hrc-cookie-policy">
      {/* Header Section */}
      <div className="hrc-cookie-header">
        <div className="hrc-cookie-header-content">
          <div className="hrc-cookie-icon">
            <FaCookie />
          </div>
          <h1 className="hrc-cookie-title">Cookie Policy</h1>
          <p className="hrc-cookie-subtitle">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>

      <div className="hrc-cookie-container">
        <div className="hrc-cookie-content">
          {/* Introduction */}
          <section className="hrc-cookie-section">
            <h2>Understanding Cookies</h2>
            <p>
              At HR Copilot, we use cookies and similar technologies to enhance your experience, 
              analyze website traffic, and personalize content. This policy explains what cookies are, 
              how we use them, and how you can control your preferences.
            </p>
          </section>

          {/* Quick Summary */}
          <section className="hrc-cookie-section">
            <div className="hrc-cookie-summary">
              <h3>Quick Summary</h3>
              <div className="hrc-summary-grid">
                <div className="hrc-summary-item">
                  <div className="hrc-summary-icon">
                    <FaShieldAlt />
                  </div>
                  <div className="hrc-summary-content">
                    <h4>Essential</h4>
                    <p>Required for website functionality</p>
                    <span className="hrc-summary-status hrc-summary-required">Always Active</span>
                  </div>
                </div>
                <div className="hrc-summary-item">
                  <div className="hrc-summary-icon">
                    <FaChartBar />
                  </div>
                  <div className="hrc-summary-content">
                    <h4>Analytics</h4>
                    <p>Help us improve our services</p>
                    <span className={`hrc-summary-status ${cookiePreferences.analytics ? 'hrc-summary-active' : 'hrc-summary-inactive'}`}>
                      {cookiePreferences.analytics ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="hrc-summary-item">
                  <div className="hrc-summary-icon">
                    <FaUserLock />
                  </div>
                  <div className="hrc-summary-content">
                    <h4>Marketing</h4>
                    <p>Personalized advertising</p>
                    <span className={`hrc-summary-status ${cookiePreferences.marketing ? 'hrc-summary-active' : 'hrc-summary-inactive'}`}>
                      {cookiePreferences.marketing ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="hrc-summary-item">
                  <div className="hrc-summary-icon">
                    <FaCog />
                  </div>
                  <div className="hrc-summary-content">
                    <h4>Preferences</h4>
                    <p>Remember your settings</p>
                    <span className={`hrc-summary-status ${cookiePreferences.preferences ? 'hrc-summary-active' : 'hrc-summary-inactive'}`}>
                      {cookiePreferences.preferences ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Cookie Management */}
          <section className="hrc-cookie-section">
            <h2>Manage Your Cookie Preferences</h2>
            <p>
              You can control which types of cookies you accept. Essential cookies are always 
              required for the website to function properly.
            </p>

            <div className="hrc-cookie-controls">
              <div className="hrc-cookie-controls-header">
                <h3>Cookie Settings</h3>
                <div className="hrc-cookie-bulk-actions">
                  <button 
                    className="hrc-cookie-btn hrc-cookie-btn-secondary"
                    onClick={acceptAllCookies}
                  >
                    Accept All
                  </button>
                  <button 
                    className="hrc-cookie-btn hrc-cookie-btn-secondary"
                    onClick={rejectAllCookies}
                  >
                    Reject Non-Essential
                  </button>
                </div>
              </div>

              <div className="hrc-cookie-types">
                {cookieTypes.map((type) => (
                  <div key={type.id} className="hrc-cookie-type">
                    <div className="hrc-cookie-type-header">
                      <div className="hrc-cookie-type-info">
                        <div className="hrc-cookie-type-icon">
                          {type.icon}
                        </div>
                        <div className="hrc-cookie-type-details">
                          <h4>{type.name}</h4>
                          <p>{type.description}</p>
                          <div className="hrc-cookie-examples">
                            <strong>Examples:</strong> {type.examples.join(', ')}
                          </div>
                        </div>
                      </div>
                      <div className="hrc-cookie-toggle">
                        {type.alwaysActive ? (
                          <div className="hrc-cookie-always-active">
                            <FaCheck />
                            <span>Always Active</span>
                          </div>
                        ) : (
                          <label className="hrc-cookie-switch">
                            <input
                              type="checkbox"
                              checked={cookiePreferences[type.id]}
                              onChange={() => handleCookieToggle(type.id)}
                            />
                            <span className="hrc-cookie-slider"></span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hrc-cookie-save-section">
                <button 
                  className="hrc-cookie-btn hrc-cookie-btn-primary"
                  onClick={saveCustomPreferences}
                >
                  Save Preferences
                </button>
                <p className="hrc-cookie-save-note">
                  Your preferences will be saved for 1 year or until you clear your browser cookies.
                </p>
              </div>
            </div>
          </section>

          {/* Detailed Cookie Information */}
          <section className="hrc-cookie-section">
            <h2>Detailed Cookie Information</h2>
            <div className="hrc-cookie-table-container">
              <table className="hrc-cookie-table">
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {cookieDetails.map((cookie, index) => (
                    <tr key={index}>
                      <td>
                        <code>{cookie.name}</code>
                      </td>
                      <td>{cookie.purpose}</td>
                      <td>
                        <span className="hrc-cookie-duration">{cookie.duration}</span>
                      </td>
                      <td>
                        <span className={`hrc-cookie-badge hrc-cookie-badge-${cookie.type.toLowerCase()}`}>
                          {cookie.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Browser Controls */}
          <section className="hrc-cookie-section">
            <h2>Browser Cookie Controls</h2>
            <p>
              You can also manage cookies through your web browser settings. Here's how to access 
              these settings in popular browsers:
            </p>
            <div className="hrc-browser-controls">
              <div className="hrc-browser-item">
                <h4>Google Chrome</h4>
                <p>Settings → Privacy and security → Cookies and other site data</p>
              </div>
              <div className="hrc-browser-item">
                <h4>Mozilla Firefox</h4>
                <p>Options → Privacy & Security → Cookies and Site Data</p>
              </div>
              <div className="hrc-browser-item">
                <h4>Safari</h4>
                <p>Preferences → Privacy → Cookies and website data</p>
              </div>
              <div className="hrc-browser-item">
                <h4>Microsoft Edge</h4>
                <p>Settings → Cookies and site permissions → Cookies and site data</p>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section className="hrc-cookie-section">
            <h2>Questions & Contact</h2>
            <p>
              If you have any questions about our cookie policy or how we use cookies, 
              please contact our Data Protection Officer:
            </p>
            <div className="hrc-contact-info">
              <p><strong>Email:</strong> privacy@hrcopilot.com</p>
              <p><strong>Phone:</strong> +1 (555) 123-HELP</p>
              <p><strong>Address:</strong> Data Protection Office, HR Copilot Inc., 123 Tech Drive, San Francisco, CA 94105</p>
            </div>
          </section>

          {/* Policy Links */}
          <section className="hrc-cookie-section">
            <div className="hrc-policy-links">
              <Link to="/privacy-policy" className="hrc-policy-link">
                Privacy Policy <FaExternalLinkAlt />
              </Link>
              <Link to="/terms-of-service" className="hrc-policy-link">
                Terms of Service <FaExternalLinkAlt />
              </Link>
              <Link to="/data-processing-agreement" className="hrc-policy-link">
                Data Processing Agreement <FaExternalLinkAlt />
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;